import {addCircularTabFocus, focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {BI_MODAL_BUTTONS, BI_ORIGINS} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import classNames from 'classnames'
import React, {useEffect, useState} from 'react'
import {useAppActions, useAppSelector} from '../../../../hooks/runtime'
import {
  getShowDescriptionPlace,
  isInAccessibilityMode,
  isInDescription,
  isInMobileBasket,
} from '../../../../selectors/seating/mode'
import {getPlacesInBasket, getSelectedPlace} from '../../../../selectors/seating/places'
import {getSelectedTicketsQuantity} from '../../../../selectors/selected-tickets'
import {AccessibilityMode} from '../accessibility-mode'
import {Footer} from '../footer'
import {Plan} from '../plan'
import {PricesOverlay} from '../prices-overlay'
import {TicketDescription} from '../ticket-description'
import {Tickets} from '../tickets'
import {MobileProps} from './interfaces'
import {classes} from './mobile.st.css'

export const Mobile = ({
  onRemoveClick,
  onCheckoutClick,
  onPlaceClick,
  onCloseClick,
  selectSeatForDescription,
}: MobileProps) => {
  const [pricesOverlayOpen, setPricesOverlayOpen] = useState(false)
  const {
    clickPlace,
    resetAreaCounterValue,
    seatingMapButtonClick,
    setShowAccessibilityMode,
    setShowMobileBasket,
    closeNativeModal,
  } = useAppActions()

  const placesInBasket = useAppSelector(getPlacesInBasket)
  const showAccessibilityMode = useAppSelector(isInAccessibilityMode)
  const selectedPlace = useAppSelector(getSelectedPlace)
  const showDescription = useAppSelector(isInDescription)
  const descriptionPlace = useAppSelector(getShowDescriptionPlace)
  const placesInBasketCount = useAppSelector(getSelectedTicketsQuantity)
  const showMobileBasket = useAppSelector(isInMobileBasket)

  const onPricesOverlayClose = () => {
    setPricesOverlayOpen(false)
    seatingMapButtonClick(BI_MODAL_BUTTONS.HIDE_PRICES)
  }

  const onPricesOverlayOpen = () => {
    setPricesOverlayOpen(true)
    seatingMapButtonClick(BI_MODAL_BUTTONS.SHOW_PRICES)
  }

  const handleDescriptionClose = () => {
    selectSeatForDescription(null)
    resetAreaCounterValue({place: descriptionPlace})
  }

  useEffect(() => {
    focusElement({selector: hookToAttributeSelector(DH.MODAL_HEADER_TITLE)})
  }, [])

  useEffect(() => {
    if (!showAccessibilityMode) {
      const removeCircularFocus = addCircularTabFocus(
        hookToAttributeSelector(DH.CLOSE_BUTTON),
        hookToAttributeSelector(selectedPlace ? DH.ADD : DH.REVIEW_AND_CHECKOUT),
      )

      return () => removeCircularFocus?.()
    }
  }, [showAccessibilityMode, selectedPlace])

  return (
    <div
      className={classNames(classes.container, {
        [classes.listContainer]: showAccessibilityMode,
        [classes.height]: showAccessibilityMode,
      })}
      onKeyDown={e => {
        e.stopPropagation()
        if (e.key === 'Escape') {
          if (showMobileBasket) {
            setShowMobileBasket(false)
          } else if (showAccessibilityMode) {
            setShowAccessibilityMode(false)
          } else if (pricesOverlayOpen) {
            setPricesOverlayOpen(false)
          } else {
            closeNativeModal()
          }
        }
      }}
    >
      <Tickets
        placesInBasketCount={placesInBasketCount}
        showAccessibilityMode={showAccessibilityMode}
        placesInBasket={placesInBasket}
        show={showMobileBasket}
        onBackClick={() => {
          setShowMobileBasket(false)
          focusElement({
            selector: hookToAttributeSelector(showAccessibilityMode ? DH.VIEW_BASKET : DH.REVIEW_AND_CHECKOUT),
            canFocus: true,
          })
        }}
        onRemoveClick={onRemoveClick}
        onCheckoutClick={onCheckoutClick}
        onTicketMoreInfoClick={selectSeatForDescription}
      />
      <TicketDescription
        showAccessibilityMode={showAccessibilityMode}
        show={showDescription}
        place={descriptionPlace}
        onClose={handleDescriptionClose}
        onTicketClick={() =>
          clickPlace({
            place: descriptionPlace,
            origin: BI_ORIGINS.SEATINGS_MODAL_TICKET_DESCRIPTION,
            updateShownDescription: true,
          })
        }
      />
      {showAccessibilityMode ? (
        <AccessibilityMode
          onFooterButtonClick={() => setShowMobileBasket(true)}
          returnFocusElement={hookToAttributeSelector(DH.ACCESSIBILITY_MODE_BUTTON)}
        />
      ) : (
        <>
          <Plan onPlaceClick={onPlaceClick} forcedPopoverPlanPlaceId={null} />
          <Footer
            showAccessibilityMode={showAccessibilityMode}
            onPricesOverlayOpen={onPricesOverlayOpen}
            withoutBreakdown={true}
            selectedPlace={selectedPlace}
            inDrawer={true}
            onCloseClick={onCloseClick}
            onCheckoutClick={() => setShowMobileBasket(true)}
          />
          <PricesOverlay pricesOverlayOpen={pricesOverlayOpen} onClose={onPricesOverlayClose} />
        </>
      )}
    </div>
  )
}
